import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { db, getMeta, type OutboxOp } from '../data/db';
import { dateTime } from '../lib/format';
import { isOnline } from '../sync/connectivity';
import { requestSync } from '../sync/engine';
import { discardOp, outboxOwner, resolveConflict, retryOp } from '../sync/outbox';
import { statusLabel, useSyncStatus } from '../sync/status';
import { useAuth } from '../auth/AuthProvider';
import { Modal } from '../ui/Modal';

/**
 * Connection and sync indicator. A small dot in the bottom-right corner while everything is fine (green online,
 * amber offline, with a count of changes waiting); it expands into a labelled pill only when someone needs to
 * act. Clicking it opens the sync review, which lists every queued change.
 */
export function OfflineStatus() {
  const status = useSyncStatus();
  const [reviewOpen, setReviewOpen] = useState(false);
  const label = statusLabel(status);
  const waiting = status.pending;
  const needsReview = status.conflicts + status.rejected + status.paused;
  const classes = ['sync-status', status.connection === 'offline' ? 'offline' : '', waiting ? 'has-work' : '', needsReview ? 'needs-review' : '', status.syncing ? 'syncing' : ''].filter(Boolean).join(' ');
  return (
    <>
      <section id="offlineStatus" className={classes} aria-live="polite" title={label} role="button" tabIndex={0} aria-label={`${label} Open sync review.`}
        onClick={() => setReviewOpen(true)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setReviewOpen(true); } }}>
        <span className="offline-status-dot" aria-hidden="true"></span>
        <span data-offline-label>{label}</span>
        <span className="offline-status-count" hidden={!(needsReview || waiting)}>{needsReview || waiting}</span>
        <span className="offline-status-action" aria-hidden="true">Review</span>
      </section>
      {reviewOpen && <SyncReview onClose={() => setReviewOpen(false)} />}
    </>
  );
}

function describe(op: OutboxOp, sessionPaused: boolean) {
  if (op.status === 'conflict') return 'Changed on another device while you were working. Choose which version to keep.';
  if (op.status === 'rejected') return `Not accepted by the server: ${op.lastError || 'unknown reason'}.`;
  if (sessionPaused) return 'Sign in to resume sync.';
  if (op.attempts && op.lastError) return `Waiting to sync. Last attempt: ${op.lastError}`;
  return 'Waiting to sync.';
}

function SyncReview({ onClose }: { onClose(): void }) {
  const status = useSyncStatus();
  const { sessionExpired } = useAuth();
  const owner = outboxOwner();
  const ops = useLiveQuery(() => db.outbox.where('ownerId').equals(owner).sortBy('seq'), [owner]) ?? [];
  const [lastSynced, setLastSynced] = useState('');
  useEffect(() => { getMeta<string>('last-synced-at', '').then(setLastSynced); }, [status.syncing]);

  return (
    <Modal id="offlineReview" className="sync-review" onClose={onClose} labelledBy="offlineReviewTitle">
      <header className="modal-header">
        <p className="eyebrow">Offline work</p>
        <h2 id="offlineReviewTitle">Sync review</h2>
        <p className="form-help">{statusLabel(status)}{lastSynced ? ` Last synced ${dateTime(lastSynced)}.` : ''}</p>
      </header>
      <div className="sync-list" data-offline-list>
        {ops.length ? ops.map((op, index) => (
          <article className={`sync-item${op.status !== 'pending' ? ` ${op.status}` : ''}`} key={op.seq}>
            <div className="sync-item-text"><b>{index + 1}. {op.label}</b><small>{describe(op, sessionExpired)}</small><small>Saved {dateTime(op.createdAt)}</small></div>
            {op.status === 'conflict' && <div className="button-group"><button className="btn btn-secondary btn-sm" onClick={() => resolveConflict(op.seq!, 'server')}>Keep server</button><button className="btn btn-primary btn-sm" onClick={() => resolveConflict(op.seq!, 'mine').then(() => requestSync())}>Use offline change</button></div>}
            {op.status === 'rejected' && <div className="button-group"><button className="btn btn-secondary btn-sm" onClick={() => { if (window.confirm(`Discard "${op.label}"? This change will be lost.`)) discardOp(op.seq!); }}>Discard</button><button className="btn btn-primary btn-sm" onClick={() => retryOp(op.seq!).then(() => requestSync())}>Retry</button></div>}
          </article>
        )) : <p className="empty-state">No offline field work is waiting to sync.</p>}
      </div>
      {status.otherAccounts > 0 && <p className="form-help">{status.otherAccounts} change{status.otherAccounts === 1 ? '' : 's'} from another account on this device will sync when that person next signs in.</p>}
      {ops.length > 1 && <p className="form-help">Changes are sent in the order shown. Anything after an item that needs review waits until it is resolved.</p>}
      <div className="form-actions">
        {status.pending > 0 && <button className="btn btn-primary" disabled={!isOnline() || status.syncing} onClick={() => requestSync()}>{status.syncing ? 'Syncing…' : 'Sync now'}</button>}
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}
